import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);

  useEffect(() => {
    // Verify the recovery token and create a session
    verifyRecoveryToken();
  }, []);

  const verifyRecoveryToken = async () => {
    try {
      // Extract token from URL - check BOTH query params and hash
      const queryParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = queryParams.get('type') || hashParams.get('type');
      const token = queryParams.get('token') || hashParams.get('access_token');

      console.log('🔍 Verifying recovery token...');
      console.log('Type:', type);
      console.log('Token:', token ? 'Present' : 'Missing');

      // If no token in URL, show error
      if (!token || type !== 'recovery') {
        setError('Invalid or expired reset link. Please request a new password reset.');
        setIsValidToken(false);
        return;
      }

      // Call Supabase to verify the OTP and create a session
      console.log('📞 Calling supabase.auth.verifyOtp...');
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'recovery'
      });

      console.log('✅ VerifyOtp result:', data);
      console.log('❌ VerifyOtp error:', verifyError);

      if (verifyError || !data.session) {
        console.error('Verification failed:', verifyError);
        setError('This password reset link is invalid or has already been used. Please request a new password reset.');
        setIsValidToken(false);
        return;
      }

      // Success! Token is valid and session is created
      console.log('✅ Session created successfully!');
      setIsValidToken(true);
    } catch (err: any) {
      console.error('Error verifying recovery token:', err);
      setError('Failed to verify reset link. Please try again or request a new password reset.');
      setIsValidToken(false);
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify we have a valid session (already created by verifyOtp)
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Invalid session. Please request a new password reset.');
      }

      // Update the password
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      // Clear the recovery token from database to prevent any potential reuse
      // Note: verifyOtp() already consumes the token, but we clear it again to be safe
      try {
        await supabase.rpc('clear_recovery_token');
      } catch (clearError) {
        console.error('Failed to clear recovery token:', clearError);
        // Don't fail the password reset if token clearing fails
      }

      // Sign out the recovery session
      await supabase.auth.signOut();

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password reset successful. Please login with your new password.' }
        });
      }, 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            mx: 2,
            textAlign: 'center',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Password Reset Successful!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your password has been reset successfully. Redirecting to login...
          </Typography>
          <CircularProgress sx={{ mt: 3 }} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          mx: 2,
        }}
      >
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter your new password below
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isValidToken ? (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleResetPassword}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {password && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Password Requirements:
                </Typography>
                {passwordRequirements.map((req, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    display="block"
                    color={req.met ? 'success.main' : 'text.secondary'}
                    sx={{ ml: 2 }}
                  >
                    {req.met ? '✓' : '○'} {req.text}
                  </Typography>
                ))}
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !password || !confirmPassword}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Reset Password'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                size="small"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
}